import React, { useState, useEffect, useRef } from "react";
import ImageBlock from "./ImageBlock";
import { useReportTemplateContext } from "../ReportTemplate/ReportTemplateContext";
import { useParams } from "react-router-dom";
import { log } from "../../utils/generalConsoleLogger";
import { updateReport, searchTextRecommendationReports, getTextRecommendationSettings } from "../../../api/api";
import { cleanReportName } from "../../../utils/reportNameUtils";
import { useUser } from "../../../context/UserContext";
import { deriveCoverStatus } from "../../utils/reportingStatesNormalization";

// Minimal/borderless field language: a small uppercase micro-label and an
// underlined input (no box, no card chrome) — green on focus, whitespace
// and typography do the separating instead of a container. Shared by
// every field below.
const fieldLabelStyle = { fontSize: '0.68rem', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: '#8e8e93' };
const fieldInputStyle = { border: 'none', borderBottom: '1px solid #e5e5ea', background: 'none', fontSize: '0.98rem', color: '#1c1c1e', width: '100%', padding: '3px 0 7px', outline: 'none' };
const refreshLinkStyle = { border: 'none', background: 'none', color: '#198754', fontSize: '0.74rem', fontWeight: 600, cursor: 'pointer', padding: 0 };

const pickerTabStyle = (active) => ({
  border: "none", borderRadius: 6, padding: "6px 16px", fontSize: "0.78rem", fontWeight: 700, cursor: "pointer",
  background: active ? "#fff" : "none", color: active ? "#1c1c1e" : "#8e8e93",
  boxShadow: active ? "0 1px 2px rgba(0,0,0,0.08)" : "none",
});
const pickerEmptyStyle = { padding: 12, fontSize: "0.8rem", color: "#8e8e93" };

// Small uppercase caption line of match flags — "Olit mukana" (green, you're
// on the team), "Sama rakennustyyppi" (neutral, same building type), and the
// source report's own state when it's NOT "ready" (amber "Luonnos"/gray
// "Arkistoitu") — ready is the expected/default case so it stays unmarked,
// same "only show the exception" pattern sameRakennusluokitus already uses.
function FlagCaption({ participated, sameRakennusluokitus, state }) {
  // Same state->label mapping as AllReports.jsx's renderStatusBadge (ready/
  // archived/else-is-draft) — anything that isn't explicitly "ready" or
  // "archived" is a draft, not just the literal string "draft".
  const statusFlag = state === "ready" ? null
    : state === "archived" ? { text: "Arkistoitu", color: "#6b7280" }
    : state ? { text: "Luonnos", color: "#b45309" } : null;
  const flags = [
    participated && { text: "Olit mukana", color: "#15803d" },
    sameRakennusluokitus && { text: "Sama rakennustyyppi", color: "#3f3f46" },
    statusFlag,
  ].filter(Boolean);
  if (!flags.length) return null;
  return (
    <div style={{ fontSize: "0.68rem", fontWeight: 700, letterSpacing: "0.04em", textTransform: "uppercase", marginTop: 4 }}>
      {flags.map((f, i) => (
        <span key={f.text}>
          {i > 0 && <span style={{ color: "#c7c7cc" }}> · </span>}
          <span style={{ color: f.color }}>{f.text}</span>
        </span>
      ))}
    </div>
  );
}

// Single result row, shared by the Suositellut and Hae tabs — small leading
// icon, hover state, Rv./±year merged into one pill (green when exact match),
// the "Olit mukana" badge, and the "Sama rakennustyyppi" badge (same wording/
// styling as TextRecommendationBanner.jsx's banner badges) when applicable.
// Only the Suositellut (by-age) tab's rows carry sameRakennusluokitus —
// free-text Hae results don't compute it, same as the banner's own scope.
//
// Name and badges are deliberately NOT in the same flex-wrap row: sharing
// one meant a badge sat inline after a short name but wrapped to its own
// line after a long one, so otherwise-identical rows looked inconsistent
// depending on name length. Name always gets its own line; badges (if any)
// always get their own line below it.
function PickerRow({ r, last, onPick }) {
  const exactMatch = r.diff === 0;
  return (
    <div
      className="trs-picker-row"
      onClick={() => onPick(r)}
      style={{ padding: "11px 14px", display: "flex", alignItems: "center", gap: 16, cursor: "pointer", borderBottom: last ? "none" : "1px solid #f0f0f2" }}
    >
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 590, fontSize: "0.84rem", color: "#1d1d1f", letterSpacing: "-0.01em" }}>
          {r.kohteen_nimi || r.osoite || r.name || `Raportti ${r.id}`}
        </div>
        {r.osoite && (
          <div style={{ fontSize: "0.72rem", color: "#86868b", marginTop: 2 }}>{r.osoite}</div>
        )}
        <FlagCaption participated={r.participated} sameRakennusluokitus={r.sameRakennusluokitus} state={r.state} />
      </div>
      {(r.diff != null || r.distanceKm != null) && (
        <div style={{ textAlign: "right", flexShrink: 0, minWidth: 56 }}>
          {r.diff != null && (
            <div style={{ fontSize: "0.86rem", fontWeight: 650, color: exactMatch ? "#1d7a46" : "#1d1d1f", fontVariantNumeric: "tabular-nums" }}>
              ±{r.diff} v
            </div>
          )}
          {r.distanceKm != null && (
            <div style={{ fontSize: "0.67rem", color: "#86868b", fontVariantNumeric: "tabular-nums", marginTop: 1 }}>
              {String(r.distanceKm).replace(".", ",")} km
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Primary template report picker — lets the user search for and pin one
// specific past report as this report's text-recommendation template.
// Stored as data.primaryTemplateReportId + a label snapshot (data.primaryTemplateReportLabel)
// for display without a re-fetch, same denormalized-label pattern as
// llm_prompt_examples.block_label. Two tabs: "Suositellut" (same building-year
// matching as the recommendation banner, via candidatesByAge on the backend)
// and "Hae" (free-text search) — Suositellut is the default so the picker
// starts useful instead of blank.
function PrimaryTemplatePicker({ reportId, reportType, selectedId, selectedLabel, onPick, onClear }) {
  const [tab, setTab] = useState("suositellut");
  const [recommended, setRecommended] = useState([]);
  const [loadingRecommended, setLoadingRecommended] = useState(false);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    if (!reportId || selectedId) return;
    setLoadingRecommended(true);
    searchTextRecommendationReports({ reportId, reportType })
      .then(r => setRecommended(r.data || []))
      .catch(() => setRecommended([]))
      .finally(() => setLoadingRecommended(false));
  }, [reportId, reportType, selectedId]);

  useEffect(() => {
    if (!query.trim()) { setResults([]); return; }
    setSearching(true);
    const t = setTimeout(() => {
      searchTextRecommendationReports({ reportId, q: query.trim(), reportType })
        .then(r => setResults(r.data || []))
        .catch(() => setResults([]))
        .finally(() => setSearching(false));
    }, 300);
    return () => clearTimeout(t);
  }, [query, reportId, reportType]);

  if (selectedId) {
    return (
      <div style={{ display: "inline-flex", alignItems: "center", gap: 8, background: "#fffbeb", border: "1px solid #fde68a", borderRadius: 8, padding: "6px 10px" }}>
        <span>⭐</span>
        <span style={{ fontSize: "0.86rem", color: "#1c1c1e" }}>{selectedLabel || `Raportti ${selectedId}`}</span>
        <button type="button" onClick={onClear} title="Poista malliraportti"
          style={{ border: "none", background: "none", color: "#92400e", fontSize: "0.8rem", cursor: "pointer", padding: 0 }}>
          ✕ poista
        </button>
      </div>
    );
  }

  const pick = (r) => onPick(r.id, r.kohteen_nimi || r.osoite || r.name || `Raportti ${r.id}`);

  return (
    <div style={{ maxWidth: 460 }}>
      <style>{`.trs-picker-row:hover { background: #f8fafc; }`}</style>
      <div style={{ display: "inline-flex", background: "#f3f4f6", borderRadius: 8, padding: 3, marginBottom: 10 }}>
        <button type="button" onClick={() => setTab("suositellut")} style={pickerTabStyle(tab === "suositellut")}>Suositellut</button>
        <button type="button" onClick={() => setTab("hae")} style={pickerTabStyle(tab === "hae")}>Hae</button>
      </div>

      {tab === "suositellut" ? (
        <div style={{ border: "1px solid #e5e5ea", borderRadius: 8, overflow: "hidden" }}>
          {loadingRecommended && <div style={pickerEmptyStyle}>Haetaan…</div>}
          {!loadingRecommended && recommended.length === 0 && (
            <div style={pickerEmptyStyle}>Ei samankaltaisia raportteja löytynyt rakennusvuoden perusteella.</div>
          )}
          {!loadingRecommended && recommended.map((r, i) => (
            <PickerRow key={r.id} r={r} last={i === recommended.length - 1} onPick={pick} />
          ))}
        </div>
      ) : (
        <div>
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Hae raporttia nimellä tai kohteella…"
            className="kansilehti-input"
            style={{ ...fieldInputStyle, marginBottom: 10 }}
          />
          {query.trim() && (
            <div style={{ border: "1px solid #e5e5ea", borderRadius: 8, overflow: "hidden", maxHeight: 220, overflowY: "auto" }}>
              {searching && <div style={pickerEmptyStyle}>Haetaan…</div>}
              {!searching && results.length === 0 && <div style={pickerEmptyStyle}>Ei osumia.</div>}
              {!searching && results.map((r, i) => (
                <PickerRow key={r.id} r={r} last={i === results.length - 1} onPick={pick} />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// WK3 reports can pin a separate malliraportti per system — exact
// report_sections.type strings (ReportSectionRenderer.jsx's
// READINESS_HIDDEN_TYPES / the WK3 LLM example JSONs), display labels from
// syncPTS.js.
const WK3_TEMPLATE_SECTIONS = [
  { type: "rakennetekninen_kuntoarvio", label: "Rakennetekninen kuntoarvio" },
  { type: "lvi-jarjestelmien_kuntoarvio", label: "LVI-järjestelmien kuntoarvio" },
  { type: "sahkojarjestelmien_kuntoarvio", label: "Sähköjärjestelmien kuntoarvio" },
];

// One WK3 section's malliraportti — collapsed by default (section name +
// current pick or "Ei valittu" + a Muuta/Valitse button); expands the same
// PrimaryTemplatePicker used for the report-wide pick inline below, and
// collapses itself again once something is picked or cleared.
function SectionTemplateRow({ label, selected, reportId, reportType, onPick, onClear, last }) {
  const [open, setOpen] = useState(false);

  return (
    <div style={{ padding: "10px 0", borderBottom: last ? "none" : "1px solid #f1f3f5" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
        <div>
          <div style={{ fontSize: "0.86rem", fontWeight: 600, color: "#1c1c1e" }}>{label}</div>
          {selected ? (
            <div style={{ fontSize: "0.76rem", color: "#92400e", marginTop: 2 }}>⭐ {selected.label || `Raportti ${selected.id}`}</div>
          ) : (
            <div style={{ fontSize: "0.76rem", color: "#adb5bd", marginTop: 2 }}>Ei valittu</div>
          )}
        </div>
        <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
          {selected && !open && (
            <button type="button" onClick={onClear} title="Poista valinta"
              style={{ border: "none", background: "none", color: "#92400e", fontSize: "0.74rem", fontWeight: 600, padding: "5px 2px" }}>
              ✕ poista
            </button>
          )}
          <button type="button" onClick={() => setOpen(v => !v)}
            style={{ border: "1px solid #e5e5ea", background: "#fff", color: "#4338ca", borderRadius: 6, padding: "5px 12px", fontSize: "0.74rem", fontWeight: 600, whiteSpace: "nowrap" }}>
            {open ? "Sulje" : selected ? "Muuta" : "Valitse"}
          </button>
        </div>
      </div>
      {open && (
        <div style={{ marginTop: 10 }}>
          <PrimaryTemplatePicker
            reportId={reportId}
            reportType={reportType}
            selectedId={null}
            selectedLabel={null}
            onPick={(id, pickedLabel) => { onPick(id, pickedLabel); setOpen(false); }}
            onClear={onClear}
          />
        </div>
      )}
    </div>
  );
}

// 🔄 Small reusable input with refresh button
function RefreshableInput({ label, value, onChange, onBlur, placeholder, onRefresh }) {
  return (
    <div style={{ marginBottom: 26, flex: 1 }}>
      <div className="d-flex justify-content-between align-items-baseline mb-1">
        <label style={fieldLabelStyle}>{label}</label>
        <button type="button" onClick={onRefresh} style={refreshLinkStyle} title="Päivitä rakennuksen tiedoista">
          ↻ Päivitä
        </button>
      </div>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onBlur={onBlur}
        placeholder={placeholder}
        className="kansilehti-input"
        style={fieldInputStyle}
      />
    </div>
  );
}
export default function CoverPageForm({
    data,
    onChange
  }) {

  const {
      report,
      rakennusData: rakennus,
      handleBlockBlur,
    } = useReportTemplateContext();


  const {
      id: propertyId,
    } = useParams();

  // Text recommendations (malliraportti picker below) are admin-only for
  // now, same temporary rollout gate as the banner/settings page — not yet
  // shown to everyone.
  const { meUser, localUser } = useUser();
  const isTextRecommendationsAdmin = (meUser || localUser)?.rooli === "admin";

  // Separate admin toggle (Tekstisuositukset settings page) for WK3's three
  // per-section pickers below — off means WK3 behaves like every other
  // report type, one report-wide pick only. Defaults true (today's shipped
  // behavior) until the real setting loads, so nothing flashes hidden then
  // shown for admins who already use this.
  const [wk3SectionTemplatesEnabled, setWk3SectionTemplatesEnabled] = useState(true);
  useEffect(() => {
    if (!isTextRecommendationsAdmin || report?.type !== 'wk3') return;
    getTextRecommendationSettings()
      .then(r => setWk3SectionTemplatesEnabled(r.data?.wk3_section_templates_enabled !== false))
      .catch(() => {});
  }, [isTextRecommendationsAdmin, report?.type]);

  const todayIso = new Date().toISOString().split("T")[0];

  const [title, setTitle] = useState(data?.title || cleanReportName(report.name) || "");
  const [marginCmLocal, setMarginCmLocal] = useState(String(data?.marginCm ?? 2));
  const [easyReadLayout, setEasyReadLayout] = useState(data?.easyReadLayout ?? false);

  const [propertyName, setPropertyName] = useState(
    data?.propertyName || rakennus?.kohteen_nimi || ""
  );
  const [tontinOmistaja, setTontinOmistaja] = useState(
    data?.tontinOmistaja || rakennus?.tontin_omistaja || ""
  );

  const [osoiteToimipaikka, setOsoiteToimipaikka] = useState(() => {
  const firstRakennus = rakennus?.rakennukset_fulls?.[0];
  const osoite = firstRakennus?.osoite || "";
  const toimipaikka = firstRakennus?.toimipaikka || "";
  const postinumero = firstRakennus?.postinumero || "";

    const combined =
      osoite && (toimipaikka || postinumero)
        ? `${osoite}, ${postinumero} ${toimipaikka}`.trim()
        : osoite || toimipaikka || "";

    return data?.osoiteToimipaikka || combined;
  });

  const [dateIso, setDateIso] = useState(report?.field_date ?? data?.dateIso ?? todayIso);
  // Tila mirrors the report's real state (report.state) by default. A manual
  // pick via the select below is only a one-time nudge: it's remembered as
  // long as report.state hasn't changed since, but the next real status
  // change (statusReportState below stops matching report?.state) overwrites
  // it again — same reasoning as RenderCoverPageForm.js's PDF-side fallback.
  const [status, setStatus] = useState(() =>
    data?.status && data?.statusReportState === report?.state
      ? data.status
      : deriveCoverStatus(report?.state)
  );
  const [versionNumber, setVersionNumber] = useState(data?.versionNumber || "");

  const lastSeenReportStateRef = useRef(report?.state);
  useEffect(() => {
    if (lastSeenReportStateRef.current === report?.state) return;
    lastSeenReportStateRef.current = report?.state;
    const next = deriveCoverStatus(report?.state);
    setStatus(next);
    onChange({ ...data, status: next, statusReportState: report?.state });
    handleBlockBlur?.();
  }, [report?.state]);

  useEffect(() => {
    log({context:'CoverpageForm uef triggered'})
    const firstRakennus = rakennus?.rakennukset_fulls?.[0];
    const osoite = firstRakennus?.osoite || "";
    const toimipaikka = firstRakennus?.toimipaikka || "";
    const postinumero = firstRakennus?.postinumero || "";

    const combined =
      osoite && (toimipaikka || postinumero)
        ? `${osoite}, ${postinumero} ${toimipaikka}`.trim()
        : osoite || toimipaikka || "";

    setTitle(data?.title || cleanReportName(report.name) || "");
    setPropertyName(data?.propertyName || rakennus?.kohteen_nimi || "");
    setTontinOmistaja(data?.tontinOmistaja || rakennus?.tontin_omistaja || "");
    setOsoiteToimipaikka(prev =>
      data?.osoiteToimipaikka?.trim()
        ? data.osoiteToimipaikka
        : prev || combined
    );
    setDateIso(report?.field_date ?? data?.dateIso ?? todayIso);
  }, [report.name, rakennus]);

  // Example for updating a single field
const handleFieldChange = (field, value) => {
  const cleanValue = value?.trim() ? value : undefined;
    // Update local state
    switch (field) {
      case "title": setTitle(value); break;
      case "propertyName": setPropertyName(value); break;
      case "tontinOmistaja": setTontinOmistaja(value); break;
      case "osoiteToimipaikka": setOsoiteToimipaikka(value); break;
      case "dateIso": setDateIso(value); break;
      case "versionNumber": setVersionNumber(value); break;
    }

    // Date is managed via report.field_date (updateReport on blur), not block data
    if (field === 'dateIso') return;

    // ✅ Call parent handler with updated field
    onChange({ ...data, [field]: value });
  };

  const handleDateBlur = () => {
    if (report?.id) {
      updateReport(report.id, { field_date: dateIso }).catch(console.error);
    }
    handleBlockBlur?.();
  };

  const refreshAddress = () => {
    const firstRakennus = rakennus?.rakennukset_fulls?.[0];
    const osoite = firstRakennus?.osoite || "";
    const toimipaikka = firstRakennus?.toimipaikka || "";
    const postinumero = firstRakennus?.postinumero || "";

    const combined =
      osoite && (toimipaikka || postinumero)
        ? `${osoite}, ${postinumero} ${toimipaikka}`.trim()
        : osoite || toimipaikka || "";

    // ✅ Use handleFieldChange so multi-block updates are safe
    handleFieldChange("osoiteToimipaikka", combined);
  };

  return (
    <div className="border p-4 rounded shadow-sm bg-white">
      <style>{`
        .kansilehti-input:focus { border-bottom: 1.5px solid #198754 !important; }
      `}</style>

      {/* Text recommendations: primary template report override — admin-only
          for now, same as the banner and the settings page. */}
      {isTextRecommendationsAdmin && (
        <>
          <div>
            <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 4 }}>
              <div style={fieldLabelStyle}>Tekstisuositusten malliraportti</div>
              <a href="/tekstisuositukset" target="_blank" rel="noopener noreferrer"
                style={{ fontSize: '0.72rem', fontWeight: 600, color: '#4338ca', textDecoration: 'none' }}>
                Asetukset ↗
              </a>
            </div>
            <div style={{ color: '#8e8e93', fontSize: '0.78rem', marginBottom: 12 }}>
              {report?.type === 'wk3' && wk3SectionTemplatesEnabled
                ? 'Valitse raportti, jonka tekstejä ehdotetaan ensisijaisena kaikissa muissa osioissa kuin alla luetelluissa kolmessa (rakennusvuoden tai tilan rajauksista riippumatta).'
                : 'Valitse yksi raportti, jonka tekstejä ehdotetaan aina ensisijaisena tekstikenttien suosituksissa (rakennusvuoden tai tilan rajauksista riippumatta).'}
            </div>
            <PrimaryTemplatePicker
              reportId={report?.id}
              reportType={report?.type}
              selectedId={data?.primaryTemplateReportId}
              selectedLabel={data?.primaryTemplateReportLabel}
              onPick={(id, label) => {
                onChange({ ...data, primaryTemplateReportId: id, primaryTemplateReportLabel: label });
                handleBlockBlur?.();
              }}
              onClear={() => {
                onChange({ ...data, primaryTemplateReportId: null, primaryTemplateReportLabel: null });
                handleBlockBlur?.();
              }}
            />
          </div>

          {report?.type === 'wk3' && wk3SectionTemplatesEnabled && (
            <div style={{ marginTop: 18 }}>
              <div style={{ fontSize: '0.68rem', fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', color: '#92400e', marginBottom: 6 }}>
                WK3: osiokohtaiset pohjaraportit
              </div>
              {WK3_TEMPLATE_SECTIONS.map((section, i) => (
                <SectionTemplateRow
                  key={section.type}
                  label={section.label}
                  reportId={report?.id}
                  reportType={report?.type}
                  selected={data?.sectionTemplateReports?.[section.type] || null}
                  last={i === WK3_TEMPLATE_SECTIONS.length - 1}
                  onPick={(id, label) => {
                    onChange({
                      ...data,
                      sectionTemplateReports: { ...data?.sectionTemplateReports, [section.type]: { id, label } },
                    });
                    handleBlockBlur?.();
                  }}
                  onClear={() => {
                    const next = { ...data?.sectionTemplateReports };
                    delete next[section.type];
                    onChange({ ...data, sectionTemplateReports: next });
                    handleBlockBlur?.();
                  }}
                />
              ))}
            </div>
          )}

          <div style={{ height: 1, background: '#f1f3f5', margin: '20px 0 26px' }} />
        </>
      )}

      {/* Report Title */}
      <div style={{ marginBottom: 26, maxWidth: '100%' }}>
        <label style={fieldLabelStyle}>Raportin otsikko</label>
        <input
          type="text"
          value={title}
          onChange={(e) => handleFieldChange("title", e.target.value)}
          onBlur={() => handleBlockBlur?.()}
          placeholder="Kirjoita raportin otsikko"
          className="kansilehti-input"
          style={{ ...fieldInputStyle, fontWeight: 700 }}
        />
      </div>

      {/* Kohteen nimi + Tontin omistaja, side by side */}
      <div className="d-flex flex-wrap" style={{ gap: 40 }}>
        <RefreshableInput
          label="Kohteen nimi"
          value={propertyName}
          onChange={(val) => handleFieldChange("propertyName", val)}
          onBlur={() => handleBlockBlur?.()}
          placeholder="Kirjoita kohteen nimi"
          onRefresh={() => handleFieldChange("propertyName", rakennus?.kohteen_nimi || "")}
        />
        <RefreshableInput
          label="Tontin omistaja"
          value={tontinOmistaja}
          onChange={(val) => handleFieldChange("tontinOmistaja", val)}
          onBlur={() => handleBlockBlur?.()}
          placeholder="Kirjoita tontin omistaja"
          onRefresh={() => handleFieldChange("tontinOmistaja", rakennus?.tontin_omistaja || "")}
        />
      </div>

      {/* Address + City with refresh */}
      <RefreshableInput
        label="Kohteen osoite ja toimipaikka"
        value={osoiteToimipaikka}
        onChange={(val) => handleFieldChange("osoiteToimipaikka", val)}
        onBlur={() => handleBlockBlur?.()}
        placeholder="Kirjoita osoite ja toimipaikka (esim. Kulorastaantie 4, Espoo)"
        onRefresh={() => handleFieldChange(
          "osoiteToimipaikka",
          (() => {
            const firstRakennus = rakennus?.rakennukset_fulls?.[0];
            const osoite = firstRakennus?.osoite || "";
            const toimipaikka = firstRakennus?.toimipaikka || "";
            const postinumero = firstRakennus?.postinumero || "";
            return osoite && (toimipaikka || postinumero)
              ? `${osoite}, ${postinumero} ${toimipaikka}`.trim()
              : osoite || toimipaikka || "";
          })()
        )}
      />

      {/* Tarkastuspäivä + marginaali, side by side */}
      <div className="d-flex flex-wrap" style={{ gap: 40 }}>
        <div style={{ marginBottom: 26, flex: 1 }}>
          <label style={fieldLabelStyle}>Tarkastuspäivä</label>
          <input
            type="date"
            value={dateIso}
            onChange={(e) => handleFieldChange("dateIso", e.target.value)}
            onBlur={handleDateBlur}
            className="kansilehti-input"
            style={fieldInputStyle}
          />
        </div>
        <div style={{ marginBottom: 26, flex: 1 }}>
          <label style={fieldLabelStyle}>Vasen/oikea marginaali</label>
          <div className="d-flex align-items-baseline" style={{ gap: 6, maxWidth: 120 }}>
            <input
              type="number"
              min="0.5"
              max="3"
              step="0.1"
              value={marginCmLocal}
              onChange={e => setMarginCmLocal(e.target.value)}
              onBlur={() => {
                const val = Math.min(3, Math.max(0.5, Number(marginCmLocal) || 2));
                setMarginCmLocal(String(val));
                onChange({ ...data, marginCm: val });
                handleBlockBlur?.();
              }}
              className="kansilehti-input"
              style={fieldInputStyle}
            />
            <span style={{ color: '#8e8e93', fontSize: '0.86rem', whiteSpace: 'nowrap' }}>cm</span>
          </div>
        </div>
      </div>

      {/* Tila (luonnos/lopullinen) + versionumero, side by side — shown on
          the PDF cover page itself (RenderCoverPageForm.js). */}
      <div className="d-flex flex-wrap" style={{ gap: 40 }}>
        <div style={{ marginBottom: 26, flex: 1 }}>
          <label style={fieldLabelStyle}>Tila</label>
          <select
            value={status}
            onChange={e => {
              const val = e.target.value;
              setStatus(val);
              onChange({ ...data, status: val, statusReportState: report?.state });
              handleBlockBlur?.();
            }}
            className="kansilehti-input"
            style={fieldInputStyle}
          >
            <option value="luonnos">Luonnos</option>
            <option value="lopullinen">Lopullinen</option>
          </select>
        </div>
        <div style={{ marginBottom: 26, flex: 1 }}>
          <label style={fieldLabelStyle}>Versionumero</label>
          <input
            type="text"
            value={versionNumber}
            onChange={(e) => handleFieldChange("versionNumber", e.target.value)}
            onBlur={() => handleBlockBlur?.()}
            placeholder="esim. 1.0"
            className="kansilehti-input"
            style={fieldInputStyle}
          />
        </div>
      </div>

      {/* Easy-read layout toggle */}
      <div className="d-flex align-items-center" style={{ gap: 8, marginBottom: 30 }}>
        <input
          type="checkbox"
          id="easyReadLayout"
          checked={easyReadLayout}
          onChange={e => {
            const val = e.target.checked;
            setEasyReadLayout(val);
            onChange({ ...data, easyReadLayout: val });
            handleBlockBlur?.();
          }}
        />
        <label htmlFor="easyReadLayout" style={{ fontSize: '0.86rem', color: '#1c1c1e', marginBottom: 0 }}>
          Helppolukuinen tekstinasettelu
        </label>
        <span style={{ color: '#adb5bd', fontSize: '0.76rem' }}>
          (Vie enemmän tilaa ja asettelu saattaa muuttua)
        </span>
      </div>

      <div style={{ height: 1, background: '#f1f3f5', margin: '8px 0 26px' }} />

      {/* Cover image selection */}
      <div>
        <div style={{ ...fieldLabelStyle, marginBottom: 12 }}>Kansikuva</div>
          <ImageBlock
            data={{ category: "Kansikuva", excludedImages: data?.excludedImages || [] }}
            propertyId={propertyId}
            lockedCategory
            onChange={(updated) => {
              onChange({ ...data, excludedImages: updated.excludedImages ?? [] });
            }}
          />
      </div>

    </div>
  );
}
